import { useEffect, useState } from "react";
import { Key, PencilSimple, Plus, SpinnerGap, Trash, X } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { ACCESS_PERMISSIONS, ACCOUNT_ROLES, accountRoleLabel, permLabel } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { toast } from "@/components/ui/sonner";

const EMPTY = { name: "", email: "", password: "", role: "concierge", permissions: [] };

export default function AdminAccounts() {
  const [accounts, setAccounts] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/accounts").then((r) => setAccounts(r.data)).catch(() => setAccounts([]));
  useEffect(() => {
    load();
  }, []);

  const togglePerm = (key) =>
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(key)
        ? f.permissions.filter((p) => p !== key)
        : [...f.permissions, key],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Le nom est requis");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        const { data } = await api.patch(`/admin/accounts/${editingId}`, {
          name: form.name,
          role: form.role,
          permissions: form.permissions,
          password: form.password || null,
        });
        setAccounts((list) => list.map((a) => (a.id === editingId ? data : a)));
        toast.success(`Compte de ${data.name} mis à jour`);
      } else {
        const { data } = await api.post("/admin/accounts", form);
        setAccounts((list) => [...(list || []), data]);
        toast.success(`Compte créé pour ${data.name}`);
      }
      setForm(EMPTY);
      setEditingId(null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSaving(false);
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setForm({
      name: a.name,
      email: a.email,
      password: "",
      role: a.role,
      permissions: a.permissions || [],
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY);
  };

  const remove = async (a) => {
    try {
      await api.delete(`/admin/accounts/${a.id}`);
      setAccounts((list) => list.filter((x) => x.id !== a.id));
      if (editingId === a.id) cancelEdit();
      toast.success(`Compte de ${a.name} supprimé`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const inputCls =
    "w-full rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition-colors focus:border-primary";

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_1.2fr]" data-testid="admin-accounts-tab">
      <form onSubmit={submit} className="h-fit rounded-3xl border border-black/5 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <h2 className="flex items-center gap-2 font-display text-lg font-bold tracking-tight">
          <Key size={20} weight="duotone" className="text-primary" />
          {editingId ? "Modifier l'accès" : "Créer un accès"}
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Concierge ou membre du conseil syndical : vous choisissez exactement ce que ce compte peut faire.
        </p>
        <div className="mt-5 space-y-3">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom (ex. Amira B.)" data-testid="account-name-input" className={inputCls} />
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="Email de connexion"
            data-testid="account-email-input"
            disabled={!!editingId}
            className={cn(inputCls, editingId && "opacity-50")}
          />
          <input
            type="text"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder={editingId ? "Nouveau mot de passe (vide = inchangé)" : "Mot de passe (8 caractères min.)"}
            data-testid="account-password-input"
            autoComplete="off"
            className={inputCls}
          />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="account-role-select" className={inputCls}>
            {ACCOUNT_ROLES.map((r) => (
              <option key={r.key} value={r.key} data-testid={`account-role-${r.key}`}>{r.label}</option>
            ))}
          </select>
          <div>
            <p className="mb-2 text-xs font-bold text-muted-foreground">Droits accordés</p>
            <div className="flex flex-wrap gap-2" data-testid="account-permissions">
              {ACCESS_PERMISSIONS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => togglePerm(p.key)}
                  data-testid={`account-perm-${p.key}`}
                  className={cn(
                    "rounded-full border px-4 py-2 text-xs font-bold transition-all duration-200",
                    form.permissions.includes(p.key)
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-black/10 bg-white text-foreground/60 hover:border-primary/40"
                  )}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-5 flex gap-2">
          <button
            type="submit"
            disabled={saving}
            data-testid="account-save-button"
            className="flex flex-1 items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-bold text-primary-foreground transition-colors hover:bg-[#265442] disabled:opacity-60"
          >
            {saving ? <SpinnerGap size={16} className="animate-spin" /> : editingId ? <PencilSimple size={16} weight="bold" /> : <Plus size={16} weight="bold" />}
            {editingId ? "Enregistrer les modifications" : "Créer le compte"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={cancelEdit}
              data-testid="account-cancel-edit"
              className="inline-flex items-center gap-1.5 rounded-full border border-black/10 px-5 py-3.5 text-sm font-bold transition-colors hover:border-primary hover:text-primary"
            >
              <X size={14} weight="bold" /> Annuler
            </button>
          )}
        </div>
      </form>

      <div className="space-y-3" data-testid="admin-accounts-list">
        {accounts === null && <div className="h-24 animate-pulse rounded-3xl bg-black/5" />}
        {accounts !== null && accounts.length === 0 && (
          <p className="rounded-3xl border border-black/5 bg-white p-8 text-center text-sm text-muted-foreground">
            Aucun accès délégué. Créez un compte pour le concierge ou le conseil syndical.
          </p>
        )}
        {accounts?.map((a) => (
          <div key={a.id} className="rounded-3xl border border-black/5 bg-white p-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`account-row-${a.id}`}>
            <div className="flex items-center gap-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <Key size={18} weight="duotone" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold">
                  {a.name} <span className="ml-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-bold text-secondary-foreground">{accountRoleLabel(a.role)}</span>
                </p>
                <p className="truncate text-xs text-muted-foreground">{a.email}</p>
              </div>
              <button
                onClick={() => startEdit(a)}
                data-testid={`account-edit-${a.id}`}
                aria-label={`Modifier ${a.name}`}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-black/10 text-foreground/50 transition-colors hover:border-primary hover:text-primary"
              >
                <PencilSimple size={16} weight="duotone" />
              </button>
              <button
                onClick={() => remove(a)}
                data-testid={`account-delete-${a.id}`}
                aria-label={`Supprimer ${a.name}`}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-black/10 text-foreground/50 transition-colors hover:border-[#C90A0A] hover:text-[#C90A0A]"
              >
                <Trash size={16} weight="duotone" />
              </button>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {(a.permissions || []).length === 0 && (
                <span className="text-[11px] font-semibold text-muted-foreground">Aucun droit pour le moment</span>
              )}
              {(a.permissions || []).map((p) => (
                <span key={p} className="rounded-full bg-[#E2F6ED] px-2.5 py-0.5 text-[10px] font-bold text-[#007A4D]">
                  {permLabel(p)}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
